// src/services/personalMoviesService.ts
import { supabase } from '../lib/supabase'
import { sanitizePersonalMovies, sanitizePersonalMovie } from '../utils/personalMovieUtils'

export interface PersonalMovie {
  id: string;
  user_id: string;
  title: string;
  color: string;
  emoji: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePersonalMovieData {
  title: string;
  color: string;
  emoji: string;
}

export interface UpdatePersonalMovieData {
  title?: string;
  color?: string;
  emoji?: string;
}

export const createPersonalMoviesService = () => {
  return {
    async createPersonalMovie(userId: string, data: CreatePersonalMovieData): Promise<PersonalMovie> {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const { data: result, error } = await supabase
        .from('personal_movies')
        .insert({
          title: data.title,
          color: data.color,
          emoji: data.emoji,
          user_id: userId
        })
        .select()
      
      if (error) {
        console.error('Error creating personal movie:', error);
        throw error;
      }
      
      // If we got a result from the insert, use it
      if (result && Array.isArray(result) && result.length > 0) {
        const createdMovie = result[0];
        const sanitizedMovie = sanitizePersonalMovie(createdMovie);
        if (sanitizedMovie) {
          return sanitizedMovie;
        }
      }
      
      // If insert succeeded but didn't return data (common with RLS),
      // fetch the most recent movie for this user
      const { data: latestMovies, error: fetchError } = await supabase
        .from('personal_movies')
        .select('*')
        .eq('user_id', userId)
        .eq('title', data.title)
        .order('created_at', { ascending: false })
        .limit(1)
      
      if (fetchError) {
        console.error('Error fetching created movie:', fetchError);
        throw new Error('Movie was created but could not be retrieved');
      }
      
      if (!latestMovies || latestMovies.length === 0) {
        throw new Error('Movie was created but could not be found');
      }
      
      const createdMovie = latestMovies[0];
      const sanitizedMovie = sanitizePersonalMovie(createdMovie);
      if (!sanitizedMovie) {
        console.error('Invalid movie data after creation:', createdMovie);
        throw new Error('Failed to create personal movie: invalid response structure');
      }
      
      return sanitizedMovie;
    },

    async getUserPersonalMovies(userId: string): Promise<PersonalMovie[]> {
      const { data, error } = await supabase
        .from('personal_movies')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      
      if (error) {
        console.error('Error fetching personal movies:', error);
        throw error;
      }
      
      return sanitizePersonalMovies(data || []);
    },

    async updatePersonalMovie(userId: string, id: string, data: UpdatePersonalMovieData): Promise<PersonalMovie> {
      // First, try to update with select to get the updated data
      const { data: result, error } = await supabase
        .from('personal_movies')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .select()

      if (error) {
        console.error('Error updating personal movie:', error);
        throw error;
      }
      
      // If we got a result from the update, use it
      if (result && Array.isArray(result) && result.length > 0) {
        const updatedMovie = result[0];
        const sanitizedMovie = sanitizePersonalMovie(updatedMovie);
        if (sanitizedMovie) {
          return sanitizedMovie;
        }
      }
      
      // If update succeeded but didn't return data (204 No Content response),
      // fetch the updated movie by id
      const { data: fetchedMovie, error: fetchError } = await supabase
        .from('personal_movies')
        .select('*')
        .eq('id', id)
        .single()
      
      if (fetchError || !fetchedMovie) {
        console.warn('Error fetching updated movie, constructing response');
        return {
          id,
          user_id: userId,
          title: data.title || 'Updated Movie',
          color: data.color || '#3b82f6',
          emoji: data.emoji || '🎬',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      
      const sanitizedMovie = sanitizePersonalMovie(fetchedMovie);
      if (!sanitizedMovie) {
        console.warn('Invalid movie data after update, constructing response:', fetchedMovie);
        return {
          id,
          user_id: userId,
          title: data.title || 'Updated Movie',
          color: data.color || '#3b82f6',
          emoji: data.emoji || '🎬',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }

      return sanitizedMovie;
    },

    async deletePersonalMovie(userId: string, id: string): Promise<void> {
      const { error } = await supabase
        .from('personal_movies')
        .delete()
        .eq('id', id)
        .eq('user_id', userId)
      
      if (error) throw error
    }
  }
}